'use client';
import { v4 as uuidv4 } from 'uuid';

import { useState, useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Plus, Loader2, ChevronDown } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useAuth } from '@/contexts/AuthContext';
import { useTenancy } from '@/contexts/TenancyContext';
import { ChecklistTemplates } from './components/checklist-templates';
import { ChecklistEditor } from './components/checklist-editor';
import { ChecklistPreview } from './components/checklist-preview';
import { ChecklistList } from './components/checklist-list';
import { ChecklistEditDialog } from './components/checklist-edit-dialog';
import { ChecklistHistoryDialog } from './components/checklist-history-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { checklistService } from './services/checklist-service';
import type { Checklist } from './types/checklist.types';
import { ChecklistProvider, useChecklistContext } from './context/checklist-context';
import type { ChecklistTemplate } from './types/checklist.types';
import { toast } from 'sonner';
import { TOUR_STEP_IDS } from '@/lib/tour-constants';
import { TourAlertDialog, useTour } from '@/components/tour';

function ChecklistsContent() {
    const [activeTab, setActiveTab] = useState('templates');
    const [isCreatingNew, setIsCreatingNew] = useState(false);
    
    // Tour state
    const { setSteps, isTourCompleted, setTourPage } = useTour();
    const [openTour, setOpenTour] = useState(false);

    const { user } = useAuth();
    const { activeTenancy } = useTenancy();

    const {
        templates,
        checklists,
        loading,
        error,
        selectedTemplate,
        setSelectedTemplate,
        createTemplate,
        updateTemplate,
        // Add setChecklists if available, or refetch after update
    } = useChecklistContext();

    const [editingChecklistId, setEditingChecklistId] = useState<string | null>(null);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
    const [selectedChecklist, setSelectedChecklist] = useState<Checklist | null>(null);
    const [templateSelectionOpen, setTemplateSelectionOpen] = useState(false);
    const location = useLocation();
    const { id: routeChecklistId } = useParams<{ id?: string }>();

    // Handle navigation state to open edit dialog
    useEffect(() => {
        const state = location.state as { editChecklistId?: string; activeTab?: string } | null;
        if (state?.editChecklistId) {
            setEditingChecklistId(state.editChecklistId);
            setEditDialogOpen(true);
            // Clear the state to prevent reopening on refresh
            window.history.replaceState({}, document.title);
        }
        if (state?.activeTab) {
            setActiveTab(state.activeTab);
        }
    }, [location.state]);

    // Deep-link: open edit dialog when route param is provided
    useEffect(() => {
        const rid = String(routeChecklistId || '').trim();
        if (!rid || !Array.isArray(checklists) || checklists.length === 0) return;
        const match = checklists.find((c) => String((c as any).id) === rid);
        if (match) {
            setEditingChecklistId((match as any).id);
            setEditDialogOpen(true);
        }
    }, [routeChecklistId, checklists]);

    // Tour implementation
    useEffect(() => {
        setTourPage('checklists');
    }, [setTourPage]);

    // Tour configuration for Checklists - dynamic like Policy Admin
    useEffect(() => {
        // Build tour steps dynamically based on what's visible
        const steps: any[] = [
            {
                content: (
                    <div>
                        <h3 className="font-semibold text-lg mb-2">Checklists</h3>
                        <p>This is your checklists page. It shows you all the checklists in your tenant.</p>
                    </div>
                ),
                selectorId: TOUR_STEP_IDS.CHECKLISTS_PAGE,
                position: "bottom" as const,
            }
        ];

        // Add Checklist button - always visible when not in edit mode
        if (!(activeTab === 'custom' && selectedTemplate)) {
            steps.push({
                content: (
                    <div>
                        <h3 className="font-semibold text-lg mb-2">Add Checklist</h3>
                        <p>This will open the add checklist modal.</p>
                    </div>
                ),
                selectorId: TOUR_STEP_IDS.ADD_CHECKLIST_BUTTON,
                position: "left" as const,
            });
        }

        // Sorting buttons - moved after Add Checklist
        steps.push({
            content: (
                <div>
                    <h3 className="font-semibold text-lg mb-2">Sort Checklists</h3>
                    <p>This will sort the checklists by selected column.</p>
                </div>
            ),
            selectorId: TOUR_STEP_IDS.CHECKLISTS_SORTING_BUTTONS,
            position: "bottom" as const,
        });

        // Action buttons - only if there are checklists
        if (checklists && checklists.length > 0) {
            steps.push({
                content: (
                    <div>
                        <h3 className="font-semibold text-lg mb-2">Checklist Actions</h3>
                        <p>These will allow you to edit, view/complete, and delete checklists.</p>
                    </div>
                ),
                selectorId: TOUR_STEP_IDS.CHECKLISTS_ACTION_BUTTONS,
                position: "left" as const,
            });
        }

        // Results per page selector
        steps.push({
            content: (
                <div>
                    <h3 className="font-semibold text-lg mb-2">Results Per Page</h3>
                    <p>This will change the number of results per page.</p>
                </div>
            ),
            selectorId: TOUR_STEP_IDS.TASKS_RESULTS_SELECTOR,
            position: "left" as const,
        });

        // Manage Columns button
        steps.push({
            content: (
                <div>
                    <h3 className="font-semibold text-lg mb-2">Manage Columns</h3>
                    <p>This will add a column of your choosing to the table.</p>
                </div>
            ),
            selectorId: TOUR_STEP_IDS.TASKS_MANAGE_COLUMNS,
            position: "left" as const,
        });

        console.log('Checklists tour steps:', steps.length);
        setSteps(steps);

        // Auto-start tour if not completed
        if (!isTourCompleted) {
            setOpenTour(true);
        }
    }, [setSteps, isTourCompleted, setTourPage, activeTab, selectedTemplate, checklists, loading]);

    const handleEditChecklist = (checklist: Checklist) => {
        setEditingChecklistId(checklist.id);
        setEditDialogOpen(true);
    };

    const [savingChecklist, setSavingChecklist] = useState(false);
    // Assume useToast from ShadCN is available; if not, fallback to alert
    // import { useToast } from '@/components/ui/use-toast';
    // const { toast } = useToast();

    const { refreshChecklists } = useChecklistContext();

    const handleSaveChecklist = async (updated: Checklist) => {
        if (!activeTenancy?.id) return;
        setSavingChecklist(true);
        try {
            if (updated._isBlankChecklist) {
                // Remove the marker property before saving
                const { _isBlankChecklist, ...checklistToSave } = updated;
                await checklistService.createChecklist(checklistToSave, activeTenancy.id);
                if (typeof refreshChecklists === 'function') {
                    await refreshChecklists();
                }
                setEditDialogOpen(false);
                setActiveTab('');
                setSelectedTemplate(null);
                toast.success('Checklist created successfully.');
            } else {
                await checklistService.updateChecklist(updated.id, updated, activeTenancy.id);
                if (typeof refreshChecklists === 'function') {
                    await refreshChecklists();
                }
                setEditDialogOpen(false);
                setActiveTab('');
                setSelectedTemplate(null);
                toast.success('Checklist updated successfully.');
            }
        } catch (err) {
            console.error('Failed to save checklist:', err);
            toast.error('Failed to save checklist: ' + (err?.message || String(err)));
        } finally {
            setSavingChecklist(false);
        }
    };

    const handleDeleteChecklist = async (checklist: Checklist) => {
        if (!activeTenancy?.id) return;
        try {
            await checklistService.deleteChecklist(checklist.id, activeTenancy.id);
            if (typeof refreshChecklists === 'function') {
                await refreshChecklists();
            }
            toast.success('Checklist deleted successfully.');
        } catch (err) {
            console.error('Failed to delete checklist:', err);
            toast.error('Failed to delete checklist: ' + (err?.message || String(err)));
        }
    };

    const handleTemplateSelect = (template: ChecklistTemplate) => {
        setSelectedTemplate(template);
        setActiveTab('custom');
    };

    const handleNewTemplate = async () => {
        if (!user?.uid || !activeTenancy?.id) {
            console.error('User not authenticated or tenant not selected');
            return;
        }

        try {
            const newTemplate = await createTemplate({
                title: 'New Checklist Template',
                description: '',
                standard: '',
                isReusable: true,
                items: [],
                badgeClass: 'bg-secondary',
                createdBy: user.uid,
                tenantId: activeTenancy.id,
                templateId: '' // Will be set by the service
            });

            setSelectedTemplate(newTemplate);
            setIsCreatingNew(true);
            setActiveTab('custom');
        } catch (err) {
            console.error('Failed to create new template:', err);
            // Handle error (show toast/notification)
        }
    };

    const handleSaveTemplate = async (template: ChecklistTemplate) => {
        console.log('🔄 handleSaveTemplate called with:', template);
        try {
            // Check if this is a system template (tenantId: 'system')
            if (template.tenantId === 'system' || template.createdBy === 'system') {
                console.log('🔄 System template detected, creating new template for user');
                // Clone system template to user's tenant
                const { id, createdAt, ...templateData } = template;
                const newTemplate = await createTemplate({
                    ...templateData,
                    title: template.title,
                    description: template.description,
                    standard: template.standard,
                    items: template.items,
                    isReusable: template.isReusable,
                    isAssuranceTask: template.isAssuranceTask,
                    badgeClass: template.badgeClass || 'bg-secondary'
                });
                
                // Update the selected template to the new one
                setSelectedTemplate(newTemplate);
                console.log('✅ New template created:', newTemplate.id);
                toast.success('Template cloned and saved successfully!');
            } else {
                // Update existing user template
                const { id, ...templateUpdates } = template;
                console.log('🔄 Updating existing template with id:', id);
                await updateTemplate(id, templateUpdates);
                console.log('✅ Template updated successfully');
                toast.success('Template saved successfully!');
            }
            
            setActiveTab('preview');
        } catch (err) {
            console.error('❌ Failed to save template:', err);
            toast.error('Failed to save template: ' + (err?.message || String(err)));
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-4 text-red-600 bg-red-50 rounded-lg">
                Error loading checklists: {error.message}
            </div>
        );
    }

    return (
        <>
            <div id={TOUR_STEP_IDS.CHECKLISTS_PAGE} className="page-container">
                <div className="glass-card">
                    <div className="flex justify-between items-center">
                        <h1 className="text-2xl font-bold mb-4">Checklist Manager</h1>
                        {!(activeTab === 'custom' && selectedTemplate) && (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button disabled={loading}>
                                        {loading ? (
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        ) : (
                                            <Plus className="mr-2 h-4 w-4" />
                                        )}
                                        Add Checklist
                                        <ChevronDown className="ml-2 h-4 w-4" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                    <DropdownMenuItem id={TOUR_STEP_IDS.ADD_CHECKLIST_BUTTON} onClick={() => {
                                        setSelectedTemplate({
                                            id: uuidv4(),
                                            title: 'Untitled Checklist',
                                            description: '',
                                            standard: '',
                                            isReusable: false,
                                            items: [],
                                            badgeClass: 'bg-secondary',
                                            createdBy: user?.uid || '',
                                            tenantId: activeTenancy?.id || '',
                                            templateId: '',
                                            createdAt: new Date(),
                                            updatedAt: new Date(),
                                            _isBlankChecklist: true
                                        });
                                        setActiveTab('custom');
                                    }}>
                                        <Plus className="mr-2 h-4 w-4" />
                                        Add new checklist
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => setTemplateSelectionOpen(true)}>
                                        Use template
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        )}
                    </div>

                    {/* Show checklist list by default */}
                    {!(activeTab === 'custom' && selectedTemplate) && (
                        <>
                            <ChecklistList
                                checklists={checklists}
                                loading={loading}
                                error={error?.message || null}
                                onEdit={handleEditChecklist}
                                onDelete={handleDeleteChecklist}
                                onViewHistory={async (checklist) => {
                                    // Pull the latest copy from Firestore so history is up to date
                                    try {
                                        if (activeTenancy?.id) {
                                            const fresh = await checklistService.getChecklist(checklist.id, activeTenancy.id);
                                            setSelectedChecklist(fresh || checklist);
                                        } else {
                                            setSelectedChecklist(checklist);
                                        }
                                    } catch (e) {
                                        // Fallback to given object if fetch fails
                                        setSelectedChecklist(checklist);
                                    }
                                    setHistoryDialogOpen(true);
                                }}
                            />
                            <ChecklistEditDialog
                                open={editDialogOpen}
                                checklistId={editingChecklistId}
                                onOpenChange={setEditDialogOpen}
                                onSave={handleSaveChecklist}
                            />
                            {selectedChecklist && (
                                <ChecklistHistoryDialog
                                    open={historyDialogOpen}
                                    onOpenChange={setHistoryDialogOpen}
                                    checklist={selectedChecklist}
                                    items={selectedChecklist.items || []}
                                />
                            )}
                        </>
                    )}

                    {/* Checklist editor and preview flows */}
                    {activeTab === 'custom' && selectedTemplate && (
                        <>
                            <ChecklistEditor
                                template={selectedTemplate}
                                onSave={selectedTemplate._isBlankChecklist ? undefined : handleSaveTemplate}
                                onSaveChecklist={selectedTemplate._isBlankChecklist ? handleSaveChecklist : undefined}
                                onCancel={() => setActiveTab('')}
                                mode={selectedTemplate._isBlankChecklist ? 'checklist' : 'template'}
                            >
                                <Button onClick={() => setTemplateSelectionOpen(true)}>
                                    Use Template
                                </Button>
                            </ChecklistEditor>
                        </>

                    )}
                    {activeTab === 'preview' && selectedTemplate && (
                        <ChecklistPreview
                            template={selectedTemplate}
                            onBack={() => setActiveTab('custom')}
                            onComplete={() => {
                                setActiveTab('');
                                setSelectedTemplate(null);
                            }}
                        />
                    )}
                </div>
            </div>
            {/* Template Selection Dialog */}
            <Dialog open={templateSelectionOpen} onOpenChange={setTemplateSelectionOpen}>
                <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto bg-card">
                    <DialogHeader>
                        <DialogTitle>Select a Template</DialogTitle>
                    </DialogHeader>
                    <ChecklistTemplates
                        onSelectTemplate={(template) => {
                            setTemplateSelectionOpen(false);
                            // Use setTimeout to ensure dialog closes before state updates
                            setTimeout(() => {
                                handleTemplateSelect(template);
                                setActiveTab('custom');
                            }, 100);
                        }}
                        onCreateNew={() => {
                            handleNewTemplate();
                            setTemplateSelectionOpen(false);
                        }}
                        onDeleteTemplate={async (template) => {
                            try {
                                console.log('Deleting template:', template);
                                await checklistService.deleteTemplate(template.id, activeTenancy?.id || '');
                                // The templates will automatically refresh via the useTemplates hook
                                toast.success('Template deleted successfully.');
                            } catch (error) {
                                console.error('Failed to delete template:', error);
                                toast.error('Failed to delete template. Please try again.');
                            }
                        }}
                    />
                </DialogContent>
            </Dialog>
            <TourAlertDialog isOpen={openTour} setIsOpen={setOpenTour} />
        </>
    );
}

export default function ChecklistsPage() {
    return (
        <ChecklistProvider>
            <ChecklistsContent />
        </ChecklistProvider>
    );
}
